import {
  Box,
  FlexAnchor,
  FlexAnchorProps,
  Icon,
  PrimaryButton,
  PrimaryButtonProps,
  smBumps,
  TertiaryButton,
  TertiaryButtonProps,
  Typo,
} from '@wrisk/ui-components'
import React, { FC, MouseEvent, useCallback } from 'react'

import { OptionalCover, Quote } from '../../../domain'
import {
  tCovers,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { useOptionalCoverModal } from './useOptionalCoverModal'

export interface OptionalFlexAnchorProps
  extends Omit<FlexAnchorProps, 'onChange' | 'ref'> {
  coverOption: OptionalCover
  loading?: boolean
  quote: Quote
  onChange?: (coverOption: OptionalCover) => void
}

const tKey = TKey('components.optional-flex-anchor')

export const OptionalFlexAnchor: FC<OptionalFlexAnchorProps> = ({
  coverOption,
  loading,
  quote,
  onChange,
  ...props
}) => {
  const showActionButton = coverOption.available && onChange
  const icon = coverOption.active ? 'tick' : 'cross'
  const Button: FC<PrimaryButtonProps | TertiaryButtonProps> = coverOption.active
    ? TertiaryButton
    : PrimaryButton
  const actionTKey = coverOption.active ? 'remove' : 'add'

  const { t } = useWriskTranslation()

  const handleModalClick = useOptionalCoverModal(quote)

  const handleActionClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      onChange?.(coverOption)
    },
    [coverOption, onChange],
  )

  return (
    <FlexAnchor
      columnGap={smBumps}
      justifyContent='flex-start'
      onClick={() => handleModalClick(coverOption)}
      px={smBumps}
      py={3}
      variant='highlight'
      {...props}
    >
      <Icon icon={icon} />
      <Box
        display='flex'
        alignItems='center'
        flexGrow={showActionButton ? 0 : 1}
        justifyContent='space-between'
        width='100%'
        gap={smBumps}
      >
        <Box display='flex' alignItems='left' flexDirection='column' gap={1}>
          <Typo>{t(tCovers(coverOption.coverOption, 'name'))}</Typo>
          {showActionButton &&
            t(tCovers(coverOption.coverOption, 'hint'), { defaultValue: '' }) && (
              <Typo typoSize='xs' color='bodySecondary'>
                {t(tCovers(coverOption.coverOption, 'hint'))}
              </Typo>
            )}
        </Box>
        <Icon icon='info' size='tiny' />
      </Box>
      {showActionButton && (
        <Button
          ml='auto'
          layout='small'
          loading={loading}
          onClick={handleActionClick}
          data-testid={`button-optional-${coverOption.coverOption}-${actionTKey}`}
        >
          {t(tKey('actions', actionTKey))}
        </Button>
      )}
    </FlexAnchor>
  )
}
